import RouterPrompt from 'components/shared/RouterPrompt'
import { useEffect } from 'react'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'

interface LabelingDirtyPromptProps {
  /**
   * Navigations whose target path ends with one of these are not warned about.
   * Used to let the Vet page escalate into a session's /label view without a
   * false "labels will be lost" warning, since labels persist by run and carry
   * across labeling surfaces.
   */
  ignorePaths?: string | string[]
}

/**
 * Warns before navigating away (or closing the tab) when there are labels that
 * have not been exported. Labels are client-side only in v1, so leaving without
 * exporting loses them.
 */
export const LabelingDirtyPrompt = ({ ignorePaths }: LabelingDirtyPromptProps): JSX.Element => {
  const {
    labeling: { dirty },
  } = useLabelingSlice()

  useEffect(() => {
    window.onbeforeunload = dirty ? () => true : () => undefined
    return () => {
      window.onbeforeunload = () => undefined
    }
  }, [dirty])

  return (
    <RouterPrompt
      when={dirty}
      ignorePaths={ignorePaths}
      dialogProps={{
        titleLabel: 'Unsaved labels',
        description:
          'You have labels that have not been exported. They will be lost if you leave. Export first to keep them. Do you still wish to leave?',
      }}
    />
  )
}

export default LabelingDirtyPrompt
